import React, { useState, useEffect } from 'react';
import { GitCommit, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { fetchFlowFunnel } from '../services/api';

export default function FlowFunnelView({ mno, route, forceMock }) {
  const [selectedFlow, setSelectedFlow] = useState('BALANCE_ENQUIRY');
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);

  const availableFlows = [
    { id: 'BALANCE_ENQUIRY', label: 'Balance Enquiry' },
    { id: 'FUNDS_TRANSFER', label: 'Funds Transfer' },
    { id: 'MINI_STATEMENT', label: 'Mini Statement' },
  ];

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetchFlowFunnel(selectedFlow, { mno, route, forceMock })
      .then(data => {
        if (isMounted) {
          setFunnelData(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [selectedFlow, mno, route, forceMock]);

  return (
    <div className="glass-card" style={{ padding: '24px', background: '#FFFFFF' }}>
      
      {/* Header & Flow Switcher */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GitCommit size={18} color="#F84122" />
            <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#0F172A', margin: 0 }}>User Journey & Flow Funnel</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', margin: 0 }}>
            Step-by-step conversion progression and drop-off leakage analysis
          </p>
        </div>

        {/* Flow Switcher Tabs */}
        <div style={{ display: 'flex', gap: '6px', background: '#F1F5F9', padding: '4px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
          {availableFlows.map(flow => (
            <button
              key={flow.id}
              onClick={() => setSelectedFlow(flow.id)}
              style={{
                background: selectedFlow === flow.id ? 'linear-gradient(135deg, #F84122 0%, #E65100 100%)' : 'transparent',
                color: selectedFlow === flow.id ? '#FFFFFF' : '#475569',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: selectedFlow === flow.id ? '0 2px 8px rgba(248, 65, 34, 0.3)' : 'none'
              }}
            >
              {flow.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading flow funnel...</div>
      ) : !funnelData ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No funnel data recorded for this flow.</div>
      ) : (
        <div>
          
          {/* Top Funnel Summary Banner */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 18px',
            background: 'rgba(248, 65, 34, 0.06)',
            border: '1px solid rgba(248, 65, 34, 0.2)',
            borderRadius: '8px',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle2 size={16} color="#F84122" />
              <span style={{ fontSize: '13px', color: '#0F172A', fontWeight: '700' }}>
                {funnelData.flowName} Funnel
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '13px' }}>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Started: </span>
                <strong style={{ color: '#0F172A' }}>{funnelData.totalSessionsStarted?.toLocaleString()}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Completed: </span>
                <strong style={{ color: '#059669' }}>{funnelData.totalSessionsCompleted?.toLocaleString()}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Conversion: </span>
                <strong style={{ color: '#059669' }}>{funnelData.overallConversionRate}%</strong>
              </div>
            </div>
          </div>

          {/* Stepped Funnel Visual */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {funnelData.steps?.map((step, idx) => {
              const isFrictionPoint = step.dropOffRate > 5.0 && idx < funnelData.steps.length - 1;

              return (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  
                  {/* Step Card */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: isFrictionPoint ? 'rgba(239, 68, 68, 0.05)' : '#F8FAFC',
                    border: isFrictionPoint ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid #E2E8F0',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                  }}>
                    
                    {/* Step Title & Badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: 'rgba(248, 65, 34, 0.12)',
                        color: '#F84122',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '800'
                      }}>
                        {step.stepNumber}
                      </span>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>
                          {step.stepName}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          Step {step.stepNumber} in session flow
                        </div>
                      </div>
                    </div>

                    {/* Step Stats */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A' }}>
                          {step.reachedCount?.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {step.conversionRate}% reached
                        </div>
                      </div>

                      {/* Drop-off Badge */}
                      {idx < funnelData.steps.length - 1 ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          background: isFrictionPoint ? 'rgba(239, 68, 68, 0.12)' : '#F1F5F9',
                          color: isFrictionPoint ? '#DC2626' : '#64748B',
                          fontSize: '11px',
                          fontWeight: '700'
                        }}>
                          {isFrictionPoint && <AlertTriangle size={12} />}
                          <span>{step.dropOffRate}% drop ({step.dropOffCount?.toLocaleString()})</span>
                        </div>
                      ) : (
                        <div style={{
                          padding: '4px 10px',
                          borderRadius: '999px',
                          background: 'rgba(16, 185, 129, 0.12)',
                          color: '#059669',
                          fontSize: '11px',
                          fontWeight: '700'
                        }}>
                          ✓ Final Outcome
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Connecting Progress Line */}
                  <div style={{
                    width: `${Math.max(15, step.conversionRate)}%`,
                    height: '4px',
                    background: isFrictionPoint ? 'linear-gradient(90deg, #F84122, #EF4444)' : 'linear-gradient(90deg, #F84122, #10B981)',
                    borderRadius: '999px',
                    marginLeft: '44px',
                    opacity: 0.8
                  }} />

                </div>
              );
            })}
          </div>

        </div>
      )}

    </div>
  );
}
